import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import type { Instance, AnnotationsUnion, List } from 'pspdfkit'
import type { SaveLinkAnnotationPayload } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.link.types'

let intervalId: ReturnType<typeof setInterval> | null = null
interface UseLinkAnnotationPayload {
  instance: Ref<Instance | undefined>
  isLinkAnnotationOpen: Ref<boolean>
  isAnnotationOpen: Ref<boolean>
  isThumbnailsOpen: Ref<boolean>
  isSearchOpen: Ref<boolean>
  isTextAnnotationOpen: Ref<boolean>
}

interface UseLinkAnnotationReturnType {
  currentLinkAnnotation: Ref<AnnotationsUnion | null>
  showLinkAnnotationPopover: ComputedRef<boolean>
  onCreateLinkAnnotation: (annotations: List<AnnotationsUnion>) => void
  onSaveLinkAnnotation: (data: SaveLinkAnnotationPayload) => void
  onUpdateLinkAnnotation: (annotations: List<AnnotationsUnion>) => void
  onDeleteLinkAnnotation: (annotations: List<AnnotationsUnion>) => void
  onCloseLinkAnnotationPopover: () => void
  onToggleLinkAnnotation: () => void
}

export function useLinkAnnotation({
  instance,
  isLinkAnnotationOpen,
  isAnnotationOpen,
  isThumbnailsOpen,
  isSearchOpen,
  isTextAnnotationOpen,
}: UseLinkAnnotationPayload): UseLinkAnnotationReturnType {
  const currentLinkAnnotation = ref<AnnotationsUnion | null>(null)
  const showLinkAnnotationPopover = computed(() => !!currentLinkAnnotation.value)

  const onCreateLinkAnnotation = (annotations: List<AnnotationsUnion>) => {
    const annotation = annotations.first() as AnnotationsUnion
    if (annotation instanceof window.PSPDFKit.Annotations.LinkAnnotation) {
      currentLinkAnnotation.value = annotation
    }
  }

  const onSaveLinkAnnotation = (data: SaveLinkAnnotationPayload) => {
    if (
      currentLinkAnnotation.value &&
      currentLinkAnnotation.value instanceof window.PSPDFKit.Annotations.LinkAnnotation
    ) {
      const { boundingBox, pageIndex } = instance.value?.getSelectedAnnotation() ?? {}
      if (!boundingBox || pageIndex === undefined) throw new Error('No bounding box or page index')
      const action = new window.PSPDFKit.Actions.GoToAction({ pageIndex: data.customData.pageIndex || 0 }) // No other type of annotation works...
      const newAnnotation = new window.PSPDFKit.Annotations.LinkAnnotation({
        pageIndex,
        boundingBox,
        action,
        customData: data.customData,
      })

      // Replace the annotation because PSPDFKit is the worst 🤮
      instance.value?.delete(currentLinkAnnotation.value)
      instance.value?.create(newAnnotation)

      currentLinkAnnotation.value = null
    }
  }

  const onUpdateLinkAnnotation = (annotations: List<AnnotationsUnion>) => {
    const updatedAnnotation = annotations.first() as AnnotationsUnion
    if (
      updatedAnnotation instanceof window.PSPDFKit.Annotations.LinkAnnotation &&
      updatedAnnotation.id === currentLinkAnnotation.value?.id
    ) {
      currentLinkAnnotation.value = updatedAnnotation
    }
  }

  const onDeleteLinkAnnotation = (annotations: List<AnnotationsUnion>) => {
    const deletedAnnotation = annotations.first() as AnnotationsUnion
    if (
      deletedAnnotation instanceof window.PSPDFKit.Annotations.LinkAnnotation &&
      deletedAnnotation.id === currentLinkAnnotation.value?.id
    ) {
      currentLinkAnnotation.value = null
    }
  }

  const onCloseLinkAnnotationPopover = () => {
    currentLinkAnnotation.value = null
    instance.value?.setSelectedAnnotation(null)
  }

  const checkSelectedAnnotations = () => {
    if (instance.value && currentLinkAnnotation.value) {
      const selectedAnnotations = instance.value.getSelectedAnnotations()?.toJS()
      if (!selectedAnnotations?.some((ann) => ann.id === currentLinkAnnotation.value?.id)) {
        currentLinkAnnotation.value = null
      }
    }
  }

  const onToggleLinkAnnotation = () => {
    isLinkAnnotationOpen.value = !isLinkAnnotationOpen.value
    isAnnotationOpen.value = false
    isThumbnailsOpen.value = false
    isSearchOpen.value = false
    isTextAnnotationOpen.value = false

    instance.value?.setCurrentAnnotationPreset('link')

    if (isLinkAnnotationOpen.value) {
      instance.value?.setViewState((viewState) => viewState.set('interactionMode', 'LINK'))
      instance.value?.addEventListener('annotations.create', onCreateLinkAnnotation)
      instance.value?.addEventListener('annotations.update', onUpdateLinkAnnotation)
      instance.value?.addEventListener('annotations.delete', onDeleteLinkAnnotation)
    } else {
      instance.value?.setEditingAnnotation(null)
      instance.value?.removeEventListener('annotations.create', onCreateLinkAnnotation)
      instance.value?.removeEventListener('annotations.update', onUpdateLinkAnnotation)
      instance.value?.removeEventListener('annotations.delete', onDeleteLinkAnnotation)

      currentLinkAnnotation.value = null
    }
  }

  onMounted(() => {
    // keep checking if the selected annotation is still selected
    intervalId = setInterval(checkSelectedAnnotations, 1000)
  })

  onUnmounted(() => {
    if (intervalId !== null) {
      clearInterval(intervalId)
    }
  })

  return {
    currentLinkAnnotation,
    showLinkAnnotationPopover,
    onCreateLinkAnnotation,
    onSaveLinkAnnotation,
    onUpdateLinkAnnotation,
    onDeleteLinkAnnotation,
    onCloseLinkAnnotationPopover,
    onToggleLinkAnnotation,
  }
}
